/**
 * 📤 Station Upload Tab - Cetalingua FM
 *
 * ✅ AUTO-CHUNKING: Users upload ANY file size — we handle the rest.
 *   • Files ≤ 80MB: client-side MP3 conversion + single PUT (original flow)
 *   • Files > 80MB: S3 Multipart Upload in ~10MB chunks (no browser crash)
 *     MP3 proxy marked as pending for server-side processing.
 *
 * ✅ PARALLEL PART UPLOADS: Up to 4 chunks upload simultaneously
 * ✅ RETRY LOGIC: Each chunk retries up to 3 times on failure
 * ✅ ABORT SUPPORT: Cancelling mid-upload cleans up S3 multipart state
 * ✅ SITE DETAILS (optional): water/hydrophone depth, bottom, sea state, calibration
 *   → saved on the file doc as `site`, and as the station's `siteDefaults` for next time
 *
 * Cloud Functions required:
 *   - getS3PresignedUrl          (existing — single PUT for small files + proxy)
 *   - initiateMultipartUpload    (NEW)
 *   - getMultipartPartUrl        (NEW)
 *   - completeMultipartUpload    (NEW)
 *   - abortMultipartUpload       (NEW)
 */

import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, collection, addDoc, query, getDocs, deleteDoc, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase-config';
import { PROJECTS, ENVIRONMENTS, SAMPLING_RATES, HYDROPHONES, RECORDERS } from '../../../data/equipmentData';
import { getSpeciesArray, getSpeciesName } from '../../../data/speciesData';
import { Mp3Encoder } from '@breezystack/lamejs';
import SiteFields, { EMPTY_SITE, cleanSite, checkSite } from './SiteFields';
import './StationUpload.css';
import '../../portal/portal-theme.css';

// ── Sample rate from the WAV header (9 Oct 2026: a 192 kHz file was uploaded as "48 kHz"; the engine reads the
// header and is right, but the file name, the record and the app showed the wrong rate) ──
async function wavSampleRate(file) {
  if (!/\.wav$/i.test(file.name)) return null;
  try {
    const v = new DataView(await file.slice(0, 65536).arrayBuffer());
    const tag = (o) => String.fromCharCode(v.getUint8(o), v.getUint8(o + 1), v.getUint8(o + 2), v.getUint8(o + 3));
    if (v.byteLength < 36 || !['RIFF', 'RF64'].includes(tag(0)) || tag(8) !== 'WAVE') return null;
    let o = 12;
    while (o + 8 <= v.byteLength) {
      const id = tag(o); const size = v.getUint32(o + 4, true);
      if (id === 'fmt ' && o + 16 <= v.byteLength) return v.getUint32(o + 12, true);
      o += 8 + size + (size % 2);
    }
  } catch (e) { /* not readable: no check */ }
  return null;
}
const rateCode = (hz) => String(Math.round(hz / 1000));                 // 192000 -> "192" (the file-name code)

// ── Constants ──
const MONTHLY_LIMIT_BYTES = 3 * 1024 * 1024 * 1024; // 3 GB
const EXTRA_GB_PRICE_CENTS = 500;                     // $5.00
const SMALL_FILE_THRESHOLD = 80 * 1024 * 1024;        // 80 MB — browser can handle
const CHUNK_SIZE = 10 * 1024 * 1024;                   // 10 MB per multipart part
const MAX_PARALLEL_PARTS = 4;                          // concurrent chunk uploads
const MAX_RETRIES = 3;                                 // retries per chunk

const StationUpload = ({ user, stationData }) => {
  const [uploading, setUploading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileRates, setFileRates] = useState([]);       // [{ name, hz }] read from the WAV headers
  const [usageData, setUsageData] = useState({ used: 0, purchased: 0 });
  const [showSpeciesModal, setShowSpeciesModal] = useState(false);
  const [speciesSearch, setSpeciesSearch] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [chunkDetail, setChunkDetail] = useState(''); // e.g. "Part 3/12"
  const [broadcastName, setBroadcastName] = useState(''); // user-facing name
  const [uploadedFiles, setUploadedFiles] = useState([]); // existing uploads
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  // 📍 Recording site (optional) — pre-filled from the station's saved defaults
  const [site, setSite] = useState({ ...EMPTY_SITE });
  const [saveSiteDefault, setSaveSiteDefault] = useState(true);

  const fileInputRef = useRef(null);
  const abortRef = useRef(false); // allows cancelling mid-upload
  const allSpecies = getSpeciesArray();

  const [metadata, setMetadata] = useState({
    project: 'DC',
    samplingRate: '48',
    customSamplingRate: '',
    shareWithCommunity: true,
    environment: 1,
    hydrophone: '',
    recorder: '',
    species: [],
  });

  // ── Helpers ──
  const getStationId = () => {
    if (!stationData?.stationId) return null;
    return String(stationData.stationId);
  };

  useEffect(() => {
    loadUsageData();
    loadUploadedFiles();
  }, [user, stationData]);

  // 📍 Pre-fill the site details from the station's defaults
  useEffect(() => {
    const loadSiteDefaults = async () => {
      const stationId = getStationId();
      if (!stationId) return;
      try {
        const st = await getDoc(doc(db, 'stations', stationId));
        const defaults = st.exists() ? (st.data().siteDefaults || {}) : {};
        setSite({ ...EMPTY_SITE, ...defaults });
      } catch (e) {
        console.warn('Could not load site defaults:', e);
      }
    };
    loadSiteDefaults();
  }, [stationData]);

  const loadUploadedFiles = async () => {
    const stationId = getStationId();
    if (!stationId) return;
    setLoadingFiles(true);
    try {
      const filesRef = query(
        collection(db, 'stations', stationId, 'files'),
        orderBy('uploadedAt', 'desc')
      );
      const snapshot = await getDocs(filesRef);
      const files = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUploadedFiles(files);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?\n\nThe broadcast disappears from the app and your results, and its analysis is removed. Reviews already finished by researchers are kept. The recording itself stays in the Cetalingua archive.\n\nThis cannot be undone.`)) return;
    const stationId = getStationId();
    if (!stationId) return;
    setDeletingFile(fileId);
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'stations', stationId, 'files', fileId));
      // Remove from local state
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      // Note: S3 files are NOT deleted (they serve as archive).
      // To also delete from S3, call a Cloud Function here.
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete: ' + error.message);
    } finally {
      setDeletingFile(null);
    }
  };

  const loadUsageData = async () => {
    const stationId = getStationId();
    if (!stationId) return;
    try {
      const now = new Date();   // UTC month: the server counts uploads per UTC month (10 Oct 2026)
      const monthKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
      const usageRef = doc(db, 'stations', stationId, 'usage', monthKey);
      const usageDoc = await getDoc(usageRef);
      setUsageData(usageDoc.exists() ? usageDoc.data() : { used: 0, purchased: 0 });
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const handleMetadataChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMetadata(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSpeciesToggle = (speciesId) => {
    setMetadata(prev => ({
      ...prev,
      species: prev.species.includes(speciesId)
        ? prev.species.filter(id => id !== speciesId)
        : [...prev.species, speciesId],
    }));
  };

  const handleFileSelect = (e) => validateAndSetFiles(Array.from(e.target.files));
  const handleDrop = (e) => { e.preventDefault(); validateAndSetFiles(Array.from(e.dataTransfer.files)); };
  const handleDragOver = (e) => e.preventDefault();

  // ── File validation — NO size cap, only timestamp check ──
  const validateAndSetFiles = (files) => {
    const validFiles = [];
    const noTimestampFiles = [];

    files.forEach(file => {
      if (!extractTimestamp(file.name)) {
        noTimestampFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (noTimestampFiles.length > 0) {
      alert(
        `These files don't have a valid timestamp (YYYYMMDD_HHMMSS) in their filename:\n\n` +
        noTimestampFiles.join('\n') +
        `\n\nPlease rename them with the recording date and time.`
      );
    }

    setSelectedFiles(validFiles);
    // read each WAV's sample rate; when all files agree, set it (the person can still change it, then a warning shows)
    Promise.all(validFiles.map(async (f) => ({ name: f.name, hz: await wavSampleRate(f) }))).then((rates) => {
      setFileRates(rates);
      const known = [...new Set(rates.map((r) => r.hz).filter(Boolean))];
      if (known.length === 1) {
        const code = rateCode(known[0]);
        const opt = SAMPLING_RATES.find((sr) => String(sr.id) === code);
        setMetadata((prev) => (opt ? { ...prev, samplingRate: opt.id }
          : { ...prev, samplingRate: 'custom', customSamplingRate: `${code}` }));
      }
    });
  };

  /**
   * 🕒 Extract YYYYMMDD_HHMMSS from filename → UTC Date
   */
  const extractTimestamp = (filename) => {
    try {
      const match = filename.match(/(\d{8})_(\d{6})/);
      if (match) {
        const rawString = match[0];
        const d = match[1];
        const t = match[2];
        const year = parseInt(d.substring(0, 4));
        const month = parseInt(d.substring(4, 6)) - 1;
        const day = parseInt(d.substring(6, 8));
        const hours = parseInt(t.substring(0, 2));
        const minutes = parseInt(t.substring(2, 4));
        const seconds = parseInt(t.substring(4, 6));
        const utcMillis = Date.UTC(year, month, day, hours, minutes, seconds);
        const dateObj = new Date(utcMillis);
        return {
          raw: rawString,
          dateObj,
          dateFormatted: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
          timeFormatted: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
          year,
          month: month + 1,
          day,
        };
      }
    } catch (e) { /* ignore */ }
    return null;
  };

  /**
   * 🎵 Client-side MP3 conversion (only for files ≤ 80 MB)
   */
  const convertToMp3 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
          const audioBuffer = await audioContext.decodeAudioData(e.target.result);
          const mp3Encoder = new Mp3Encoder(1, 48000, 128);
          const samples = audioBuffer.getChannelData(0);
          const sampleData = new Int16Array(samples.length);
          for (let i = 0; i < samples.length; i++) {
            const s = Math.max(-1, Math.min(1, samples[i]));
            sampleData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          const mp3Data = [];
          const blockSize = 1152;
          for (let i = 0; i < sampleData.length; i += blockSize) {
            const chunk = sampleData.subarray(i, i + blockSize);
            const buf = mp3Encoder.encodeBuffer(chunk);
            if (buf.length > 0) mp3Data.push(buf);
          }
          const flush = mp3Encoder.flush();
          if (flush.length > 0) mp3Data.push(flush);
          resolve({ blob: new Blob(mp3Data, { type: 'audio/mp3' }), duration: audioBuffer.duration });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  /**
   * 🏷️ Construct filename from metadata + timestamp
   */
  const constructFilename = (originalFilename, index, type = 'standard', timestampString = null) => {
    const stationId = getStationId() || '000';
    const project = metadata.project;
    const environment = metadata.environment;
    const speciesId = metadata.species[0] || 86;
    const samplingRate = type === 'original'
      ? (metadata.samplingRate === 'custom' ? metadata.customSamplingRate : metadata.samplingRate)
      : '48';
    const share = metadata.shareWithCommunity ? '1' : '0';
    const finalTimestamp = timestampString || 'UNKNOWN';
    const ext = type === 'original'
      ? originalFilename.split('.').pop().toLowerCase()
      : 'mp3';
    return `${stationId}_${project}_${samplingRate}_${share}_${environment}_${speciesId}_${finalTimestamp}.${ext}`;
  };

  const getTotalFileSize = () => selectedFiles.reduce((sum, f) => sum + f.size, 0);
  const getAvailableSpace = () => {
    const purchased = (usageData.purchased || 0) * 1024 * 1024 * 1024;
    return Math.max(0, (MONTHLY_LIMIT_BYTES + purchased) - (usageData.used || 0));
  };
  // (was declared twice in this file, which does not compile; one copy kept)
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };
  const getUsagePercentage = () => {
    const total = MONTHLY_LIMIT_BYTES + ((usageData.purchased || 0) * 1024 * 1024 * 1024);
    return Math.min(100, ((usageData.used || 0) / total) * 100);
  };

  const handlePurchaseStorage = async () => {
    if (!purchaseAmount || purchaseAmount < 1) return alert('Please select at least 1 GB');
    setPurchasing(true);
    try {
      const functions = getFunctions();
      const createCheckout = httpsCallable(functions, 'createStorageCheckout');
      const { data } = await createCheckout({
        stationId: getStationId(),
        gigabytes: purchaseAmount,
      });
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL returned');
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout: ' + error.message);
    } finally {
      setPurchasing(false);
    }
  };

  // =====================================================================
  //  🧩 S3 MULTIPART UPLOAD — chunked upload for large files
  // =====================================================================

  /**
   * Upload a single part with retry logic.
   * Returns { PartNumber, ETag }.
   */
  const uploadPartWithRetry = async (url, blob, partNumber, retries = MAX_RETRIES) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const resp = await fetch(url, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': 'application/octet-stream' },
        });

        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        // S3 returns ETag in the response header
        const etag = resp.headers.get('ETag');
        if (!etag) throw new Error('No ETag in response');

        return { PartNumber: partNumber, ETag: etag };
      } catch (err) {
        console.warn(`⚠️ Part ${partNumber} attempt ${attempt} failed:`, err.message);
        if (attempt === retries) throw err;
        // exponential back-off: 1s, 2s, 4s
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
      }
    }
  };

  /**
   * Multipart upload: slice file → upload chunks in parallel → complete.
   * Returns the total bytes uploaded.
   */
  const multipartUpload = async (file, s3Key, bucket, onPartComplete) => {
    const functions = getFunctions();
    const initiate = httpsCallable(functions, 'initiateMultipartUpload');
    const getPartUrl = httpsCallable(functions, 'getMultipartPartUrl');
    const complete = httpsCallable(functions, 'completeMultipartUpload');
    const abort = httpsCallable(functions, 'abortMultipartUpload');

    const contentType = file.type || 'application/octet-stream';
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);

    // 1. Initiate
    // size: the server checks the monthly limit and ties each part link to its size (10 Oct 2026)
    const { data: initData } = await initiate({ bucket, key: s3Key, contentType, size: file.size });
    const uploadId = initData.uploadId;

    console.log(`📤 Multipart started: ${totalParts} parts, uploadId=${uploadId}`);

    try {
      const completedParts = [];
      let partsUploaded = 0;

      // 2. Upload parts with concurrency limit
      const partQueue = Array.from({ length: totalParts }, (_, i) => i + 1);

      const worker = async () => {
        while (partQueue.length > 0) {
          if (abortRef.current) throw new Error('Upload cancelled');

          const partNumber = partQueue.shift();
          if (!partNumber) break;

          const start = (partNumber - 1) * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);

          // Get signed URL for this part
          const { data: partData } = await getPartUrl({
            bucket,
            key: s3Key,
            uploadId,
            partNumber,
            size: end - start,
          });

          // Upload with retry
          const result = await uploadPartWithRetry(partData.url, blob, partNumber);
          completedParts.push(result);

          partsUploaded++;
          if (onPartComplete) onPartComplete(partsUploaded, totalParts);
        }
      };

      // Launch parallel workers
      const workers = [];
      for (let i = 0; i < Math.min(MAX_PARALLEL_PARTS, totalParts); i++) {
        workers.push(worker());
      }
      await Promise.all(workers);

      // 3. Complete
      await complete({ bucket, key: s3Key, uploadId, parts: completedParts });
      console.log(`✅ Multipart complete: ${s3Key}`);

      return file.size;
    } catch (err) {
      // Abort the multipart upload to clean up orphaned parts
      console.error('❌ Multipart failed, aborting:', err.message);
      try {
        await abort({ bucket, key: s3Key, uploadId });
      } catch (abortErr) {
        console.warn('Abort cleanup failed:', abortErr.message);
      }
      throw err;
    }
  };

  // =====================================================================
  //  🚀 MAIN UPLOAD HANDLER
  // =====================================================================

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return alert('Select files first.');
    if (metadata.species.length === 0) return alert('Select at least one species.');
    const siteProblem = checkSite(site);
    if (siteProblem) return alert(siteProblem);
    const siteClean = cleanSite(site);

    const stationId = getStationId();
    if (!stationId) return alert('No Station ID. Please contact support.');

    if (getTotalFileSize() > getAvailableSpace()) {
      return alert('Not enough storage space. Purchase more below.');
    }

    setUploading(true);
    setUploadProgress(0);
    abortRef.current = false;

    try {
      const functions = getFunctions();
      const getPresignedUrl = httpsCallable(functions, 'getS3PresignedUrl');

      const totalFiles = selectedFiles.length;

      for (let i = 0; i < totalFiles; i++) {
        if (abortRef.current) break;

        const file = selectedFiles[i];
        const extracted = extractTimestamp(file.name);
        if (!extracted) { console.error(`❌ No timestamp: ${file.name}`); continue; }

        const timestampStr = extracted.raw;
        const recordingDateObj = extracted.dateObj;
        const isLargeFile = file.size > SMALL_FILE_THRESHOLD;

        const year = extracted.year;
        const month = String(extracted.month).padStart(2, '0');
        const prefix = `${stationId}/${year}/${month}`;

        const proxyName = constructFilename(file.name, i, 'proxy', timestampStr);
        const originalName = constructFilename(file.name, i, 'original', timestampStr);
        const originalKey = `${prefix}/${originalName}`;

        let mp3Blob = null;
        let audioDuration = 0;

        // ── Step 1: MP3 conversion (small files only) ──
        if (!isLargeFile) {
          setProcessingStatus(`Converting ${i + 1}/${totalFiles}: ${file.name}...`);
          setChunkDetail('');
          try {
            const result = await convertToMp3(file);
            mp3Blob = result.blob;
            audioDuration = result.duration || 0;
            console.log(`✅ Converted: ${formatBytes(file.size)} → ${formatBytes(mp3Blob.size)}, ${audioDuration.toFixed(1)}s`);
          } catch (err) {
            console.warn('Conversion failed, skipping proxy:', err);
          }
        } else {
          setProcessingStatus(`Uploading ${i + 1}/${totalFiles}: ${file.name} (${formatBytes(file.size)})...`);
          console.log(`📦 Large file (${formatBytes(file.size)}) — skipping browser conversion, will use server-side proxy.`);
        }

        // ── Step 2: Upload original ──
        if (isLargeFile) {
          // ▸ MULTIPART for large files
          setChunkDetail('Starting chunked upload...');
          await multipartUpload(
            file,
            originalKey,
            'cetalinguastations',
            (done, total) => {
              setChunkDetail(`Part ${done} of ${total}`);
              // Overall progress: file-level + part-level
              const fileProgress = i / totalFiles;
              const partProgress = done / total / totalFiles;
              setUploadProgress(Math.round((fileProgress + partProgress) * 100));
            }
          );
        } else {
          // ▸ Single PUT for small files
          setProcessingStatus(`Uploading Original ${i + 1}/${totalFiles}...`);
          setChunkDetail('');
          const { data: urlData } = await getPresignedUrl({
            bucket: 'cetalinguastations',
            key: originalKey,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
          });
          const put = await fetch(urlData.url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
          });
          if (!put.ok) throw new Error(`Upload of ${file.name} failed (HTTP ${put.status}). Please try again.`);
        }

        // ── Step 3: Upload proxy MP3 (small files only) ──
        if (mp3Blob) {
          setProcessingStatus(`Uploading Proxy ${i + 1}/${totalFiles}...`);
          setChunkDetail('');
          const proxyKey = `${prefix}/${proxyName}`;
          const { data: proxyUrlData } = await getPresignedUrl({
            bucket: 'cetalinguastations',
            key: proxyKey,
            contentType: 'audio/mp3',
            size: mp3Blob.size,
          });
          const putProxy = await fetch(proxyUrlData.url, {
            method: 'PUT',
            body: mp3Blob,
            headers: { 'Content-Type': 'audio/mp3' },
          });
          if (!putProxy.ok) throw new Error(`Upload of the listening copy of ${file.name} failed (HTTP ${putProxy.status}). Please try again.`);
        }

        // ── Step 4: Save metadata to Firestore ──
        const totalUploadSize = file.size + (mp3Blob ? mp3Blob.size : 0);

        await addDoc(collection(db, 'stations', stationId, 'files'), {
          broadcastName: broadcastName || proxyName,
          displayName: proxyName,
          originalFile: originalName,
          proxyFile: mp3Blob ? proxyName : null,
          originalSize: file.size,
          proxySize: mp3Blob ? mp3Blob.size : 0,
          totalSize: totalUploadSize,
          s3KeyOriginal: originalKey,
          s3KeyProxy: mp3Blob ? `${prefix}/${proxyName}` : null,
          recordingTimestamp: recordingDateObj.toISOString(),
          duration: Math.round(audioDuration),
          metadata: { ...metadata, samplingRate: '48' },
          originalSamplingRate: metadata.samplingRate,
          uploadedAt: new Date().toISOString(),
          stationId,
          uploadedBy: user.uid,
          // ✅ NEW: Flag for server-side proxy generation
          // always 'pending' (12 Oct 2026): preprocessing (Phase 1) only takes proxyStatus 'pending' files — with 'ready'
          // a browser-converted upload was never preprocessed, so no models / engine ran. The browser's mp3 still plays
          // at once (s3KeyProxy); preprocessing replaces it with its own.
          proxyStatus: 'pending',
          browserProxy: !!mp3Blob,
          uploadMethod: isLargeFile ? 'multipart' : 'single',
          // 📍 Optional site details for the dolphin engine (positions, real sound levels)
          site: siteClean,
        });

        // ── Step 5: usage ── counted by the server when it issued the upload links (10 Oct 2026); shown below

        // ── Step 6: Update station doc ──
        const stationRef = doc(db, 'stations', stationId);
        await setDoc(stationRef, {
          lastBroadcastDate: new Date().toISOString(),
          isOnline: true,
          ...(saveSiteDefault && Object.keys(siteClean).length ? { siteDefaults: siteClean } : {}),
        }, { merge: true });

        setUsageData(prev => ({ ...prev, used: (prev.used || 0) + totalUploadSize }));
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      setSelectedFiles([]); setFileRates([]);
      setBroadcastName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadUploadedFiles();
      alert('Upload complete! Your broadcast is now live. 🎉');
    } catch (error) {
      if (abortRef.current) {
        alert('Upload cancelled.');
      } else {
        console.error(error);
        alert('Error: ' + error.message);
      }
    } finally {
      setUploading(false);
      setProcessingStatus('');
      setChunkDetail('');
      setUploadProgress(0);
      abortRef.current = false;
      loadUsageData();
    }
  };

  const handleCancelUpload = () => {
    abortRef.current = true;
    setProcessingStatus('Cancelling...');
  };

  const filteredSpecies = allSpecies.filter(s =>
    s.name.toLowerCase().includes(speciesSearch.toLowerCase()) ||
    s.scientific.toLowerCase().includes(speciesSearch.toLowerCase())
  );

  const usagePercent = getUsagePercentage();


  // =====================================================================
  //  🖥️ RENDER
  // =====================================================================

  return (
    <div className="station-upload-tab">
      {/* ── Usage Card ── */}
      <div className="usage-card">
        <div className="usage-header">
          <h3>📊 Monthly Storage</h3>
          <span className="usage-month">
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
        </div>

        <div className="usage-bar-wrapper">
          <div
            className={`usage-bar ${usagePercent > 80 ? 'warning' : ''} ${usagePercent > 95 ? 'critical' : ''}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>

        <div className="usage-stats">
          <span>{formatBytes(usageData.used || 0)} used</span>
          <span>of {formatBytes(MONTHLY_LIMIT_BYTES + (usageData.purchased || 0) * 1024 * 1024 * 1024)}</span>
        </div>

        <div className="station-id-display" style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}>
          Station ID: <code>{getStationId() || 'Not assigned'}</code>
        </div>

        <div className="purchase-section">
          <h4>Need more storage?</h4>
          <div className="purchase-row">
            <div className="quantity-control">
              <button onClick={() => setPurchaseAmount(Math.max(1, purchaseAmount - 1))}>−</button>
              <span>{purchaseAmount} GB</span>
              <button onClick={() => setPurchaseAmount(purchaseAmount + 1)}>+</button>
            </div>
            <span className="purchase-price">${purchaseAmount * 5}</span>
            <button className="purchase-btn" onClick={handlePurchaseStorage} disabled={purchasing}>
              {purchasing ? 'Processing...' : 'Buy Now'}
            </button>
          </div>
          <p className="purchase-note">$5 per GB • Added instantly • Valid this month</p>
        </div>
      </div>

      <div className="upload-columns">
        {/* ── Metadata Form ── */}
        <div className="metadata-form">
          <h3>📝 Recording Metadata</h3>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Broadcast Name *</label>
              <input
                type="text"
                value={broadcastName}
                onChange={(e) => setBroadcastName(e.target.value)}
                placeholder="e.g., Morning chorus - Tampa Bay"
                className="broadcast-name-input"
              />
              <small style={{ color: '#666', fontStyle: 'italic' }}>
                This name will appear in the app for listeners.
              </small>
            </div>

            <div className="form-group">
              <label>Project *</label>
              <select name="project" value={metadata.project} onChange={handleMetadataChange}>
                {PROJECTS.map(p => (
                  <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Recording Date</label>
              <div className="auto-field">
                <span className="auto-badge">📅 Auto-detected from filename</span>
              </div>
            </div>

            <div className="form-group">
              <label>Original Sampling Rate *</label>
              <select name="samplingRate" value={metadata.samplingRate} onChange={handleMetadataChange}>
                {SAMPLING_RATES.map(sr => (
                  <option key={sr.id} value={sr.id}>{sr.label}</option>
                ))}
              </select>
              {(() => {
                const known = [...new Set(fileRates.map((r) => r.hz).filter(Boolean))];
                if (!known.length) return null;
                const chosen = metadata.samplingRate === 'custom' ? String(metadata.customSamplingRate).replace(/[^0-9.]/g, '') : String(metadata.samplingRate);
                if (known.length > 1) {
                  return <small className="upload-rate-warn">⚠️ The selected files have different sample rates ({known.map((h) => `${h / 1000} kHz`).join(', ')}). Upload each rate as its own batch.</small>;
                }
                const code = rateCode(known[0]);
                return chosen === code
                  ? <small className="upload-rate-ok">✓ Read from the file: {known[0] / 1000} kHz</small>
                  : <small className="upload-rate-warn">⚠️ The file says {known[0] / 1000} kHz, but {chosen || '?'} kHz is selected. The analysis uses the file's rate; please select {known[0] / 1000} kHz so the record is right.</small>;
              })()}
            </div>

            <div className="form-group full-width">
              <small style={{ color: '#666', fontStyle: 'italic' }}>
                * Note: A 48kHz copy will be automatically created for playback.
              </small>
            </div>

            {metadata.samplingRate === 'custom' && (
              <div className="form-group">
                <label>Custom Rate</label>
                <input
                  type="text"
                  name="customSamplingRate"
                  value={metadata.customSamplingRate}
                  onChange={handleMetadataChange}
                  placeholder="e.g., 44kHz"
                />
              </div>
            )}

            <div className="form-group">
              <label>Environment *</label>
              <select
                name="environment"
                value={metadata.environment}
                onChange={(e) => setMetadata(prev => ({ ...prev, environment: parseInt(e.target.value) }))}
              >
                {ENVIRONMENTS.map(env => (
                  <option key={env.id} value={env.id}>{env.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Hydrophone</label>
              <select name="hydrophone" value={metadata.hydrophone} onChange={handleMetadataChange}>
                <option value="">Select...</option>
                {HYDROPHONES.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Recorder</label>
              <select name="recorder" value={metadata.recorder} onChange={handleMetadataChange}>
                <option value="">Select...</option>
                {RECORDERS.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            {/* Public / private (Stage 3.6, 1 Oct 2026) — stored as metadata.shareWithCommunity */}
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Who can listen to and analyse this broadcast? *</label>
              <div className="ct-grid2" style={{ marginTop: 6 }}>
                {[
                  { v: true, t: '🌍 Public', d: 'Shared with the community: everyone can listen in the Cetalingua FM app, and decoders / researchers can analyse it.' },
                  { v: false, t: '🔒 Private', d: 'Only you (your station login in the app) can listen, and only analysts working under your station can analyse it.' },
                ].map((o) => (
                  <label key={o.t} className="ct-card" style={{ cursor: 'pointer', marginBottom: 0,
                    borderColor: metadata.shareWithCommunity === o.v ? 'var(--accent, #00b4d8)' : undefined,
                    boxShadow: metadata.shareWithCommunity === o.v ? '0 0 0 1px var(--accent, #00b4d8)' : 'none' }}>
                    <span className="ct-check" style={{ margin: 0 }}>
                      <input type="radio" name="shareWithCommunity" style={{ width: 'auto', margin: 0, flex: 'none' }} checked={metadata.shareWithCommunity === o.v}
                             onChange={() => setMetadata((m) => ({ ...m, shareWithCommunity: o.v }))} />
                      <strong>{o.t}</strong>
                    </span>
                    <span className="ct-muted" style={{ display: 'block', marginTop: 6 }}>{o.d}</span>
                  </label>
                ))}
              </div>
              <p className="ct-muted" style={{ marginTop: 6 }}>You can change this later for each broadcast (Results tab).</p>
            </div>
          </div>

          <div className="species-select">
            <label>Species * <span className="hint">(select at least 1)</span></label>
            <button type="button" className="species-btn" onClick={() => setShowSpeciesModal(true)}>
              {metadata.species.length > 0
                ? `${metadata.species.length} species selected`
                : '+ Select species...'}
            </button>
            {metadata.species.length > 0 && (
              <div className="selected-species">
                {metadata.species.map(id => (
                  <span key={id} className="species-tag">
                    {getSpeciesName(id)}
                    <button type="button" onClick={() => handleSpeciesToggle(id)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* 📍 Optional: lets the dolphin engine work out distance, depth and real sound levels */}
          <SiteFields
            site={site}
            onChange={setSite}
            showSaveDefault
            saveDefault={saveSiteDefault}
            onSaveDefault={setSaveSiteDefault}
          />
        </div>

        {/* ── File Upload Section ── */}
        <div className="file-upload-section">
          <h3>📁 Select Files</h3>

          <div className="filename-hint">
            <span>📋 Files must have timestamp in name:</span>
            <code>*_YYYYMMDD_HHMMSS.*</code>
            <span className="example">e.g., recording_20250602_093630.wav</span>
          </div>

          <div
            className={`drop-zone ${selectedFiles.length > 0 ? 'has-files' : ''}`}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".wav,.flac,.mp3,.aiff,.ogg,.mp4"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {selectedFiles.length === 0 ? (
              <>
                <div className="drop-icon">📤</div>
                <p>Click or drag files here</p>
                <span className="drop-formats">Any file size — we handle the rest</span>
              </>
            ) : (
              <>
                <div className="drop-icon">✅</div>
                <p>{selectedFiles.length} file(s) selected</p>
                <span className="drop-formats">{formatBytes(getTotalFileSize())}</span>
              </>
            )}
          </div>

          {/* File List */}
          {selectedFiles.length > 0 && (
            <div className="file-list">
              <h4>Files to upload:</h4>
              {selectedFiles.map((file, i) => {
                const ts = extractTimestamp(file.name);
                const isLarge = file.size > SMALL_FILE_THRESHOLD;
                return (
                  <div key={i} className="file-row">
                    <div className="file-info">
                      <span className="file-original">{file.name}</span>
                      <span className="file-timestamp">
                        {ts && <>📅 {ts.dateFormatted} ⏰ {ts.timeFormatted} UTC</>}
                        {isLarge && <span className="file-large-badge">Chunked upload</span>}
                      </span>
                    </div>
                    <span className="file-arrow">→</span>
                    <div className="file-dual-output">
                      <span className="file-new">{constructFilename(file.name, i, 'proxy', ts?.raw)}</span>
                      <span className="file-subtext">
                        + Original Archive ({formatBytes(file.size)})
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload / Cancel buttons */}
          <div className="upload-actions">
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0 || metadata.species.length === 0}
            >
              {uploading
                ? processingStatus || `Uploading... ${Math.round(uploadProgress)}%`
                : `📤 Upload ${selectedFiles.length > 0 ? selectedFiles.length + ' File(s)' : ''}`}
            </button>

            {uploading && (
              <button className="cancel-btn" onClick={handleCancelUpload}>
                ✕ Cancel
              </button>
            )}
          </div>

          {/* Progress */}
          {uploading && (
            <div className="upload-progress-section">
              <div className="progress-bar-wrapper">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
              </div>
              {chunkDetail && (
                <div className="chunk-detail">{chunkDetail}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Uploaded Files List ── */}
      <div className="uploaded-files-section">
        <h3>📂 Uploaded Broadcasts</h3>
        {loadingFiles ? (
          <p style={{ color: '#888' }}>Loading...</p>
        ) : uploadedFiles.length === 0 ? (
          <p style={{ color: '#888' }}>No broadcasts uploaded yet.</p>
        ) : (
          <div className="uploaded-files-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Activity</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map(file => (
                  <tr key={file.id}>
                    <td className="file-name-cell">
                      <span className="broadcast-name">
                        {file.broadcastName || file.displayName || file.originalFile}
                      </span>
                      <span className="file-technical-name">
                        {file.originalFile}
                      </span>
                    </td>
                    <td className="file-date-cell">
                      {file.recordingTimestamp
                        ? new Date(file.recordingTimestamp).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })
                        : '—'}
                    </td>
                    <td>{formatBytes(file.originalSize || 0)}</td>
                    <td>
                      <span className={`status-badge status-${
                        file.processingStatus === 'complete' || file.processingStatus === 'localized' ? 'complete' :
                        file.proxyStatus === 'error' || (file.processingStatus || '').includes('error') ? 'error' :
                        file.proxyStatus === 'ready' ? 'ready' : 'pending'
                      }`}>
                        {file.processingStatus === 'complete' ? '✅ Complete' :
                         file.processingStatus === 'localized' ? '🐬 Analysed' :
                         file.proxyStatus === 'error' ? '❌ Error' :
                         (file.processingStatus || '').includes('error') ? '❌ Error' :
                         file.processingStatus === 'detected' ? '📡 Detected' :
                         file.processingStatus === 'preprocessed' ? '🔄 Processing' :
                         file.proxyStatus === 'ready' ? '✅ Ready' :
                         file.proxyStatus === 'pending' ? '⏳ Queued for analysis' : '—'}
                      </span>
                    </td>
                    <td>
                      {/* 13 Oct 2026: both analyses (before: the detection model only, so files where only the engine
                          found sounds showed 0 %) */}
                      {file.engineSummary?.activityPercent != null || file.detectionSummary?.activityPercent != null ? (
                        <span className="activity-two">
                          {file.engineSummary?.activityPercent != null && <span title="Acoustic analysis (engine): share of the recording with animal sounds">🔊 Acoustic {file.engineSummary.activityPercent}%</span>}
                          {file.detectionSummary?.activityPercent != null && file.modelStatus !== 'no_model' && <span title="Detection model: share of the recording with calls">🎯 Model {file.detectionSummary.activityPercent}%</span>}
                          {file.modelStatus === 'no_model' && <span className="activity-nomodel" title="No detection model for this species yet — the acoustic engine analyses it">🎯 No model yet</span>}
                        </span>) : (file.modelStatus === 'no_model' ? <span className="activity-nomodel" title="No detection model for this species yet — the acoustic engine analyses it">Acoustic analysis only</span> : '—')}
                    </td>
                    <td>
                      <button
                        className="delete-file-btn"
                        onClick={() => handleDeleteFile(file.id,
                          file.broadcastName || file.displayName || file.originalFile)}
                        disabled={deletingFile === file.id}
                      >
                        {deletingFile === file.id ? '...' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Species Modal ── */}
      {showSpeciesModal && (
        <div className="modal-backdrop" onClick={() => setShowSpeciesModal(false)}>
          <div className="species-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Species</h3>
              <button onClick={() => setShowSpeciesModal(false)}>×</button>
            </div>
            <input
              type="text"
              className="species-search"
              placeholder="Search species..."
              value={speciesSearch}
              onChange={(e) => setSpeciesSearch(e.target.value)}
            />
            <div className="species-list">
              {['whale', 'dolphin', 'manatee', 'porpoise', 'unknown'].map(category => {
                const catSpecies = filteredSpecies.filter(s => s.category === category);
                if (catSpecies.length === 0) return null;
                return (
                  <div key={category} className="species-category">
                    <h4>{category.charAt(0).toUpperCase() + category.slice(1)}s</h4>
                    {catSpecies.map(sp => (
                      <label key={sp.id} className="species-option">
                        <input
                          type="checkbox"
                          checked={metadata.species.includes(sp.id)}
                          onChange={() => handleSpeciesToggle(sp.id)}
                        />
                        <span>{sp.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <span>{metadata.species.length} selected</span>
              <button onClick={() => setShowSpeciesModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationUpload;
